import { useState } from 'react';
import { isImportJobFinished } from '../../models/masterImport.types';
import type { MasterImportJob } from '../../models/masterImport.types';
import { useImportProgress } from './ImportProgressContext';
import type { TrackedImport } from './ImportProgressContext';

/**
 * The bottom-right tray that follows queued imports.
 *
 * <p>Mounted once at the layout, so it survives the Import dialog closing and the user navigating to
 * another screen — which is the whole promise the dialog makes when it says the import keeps running.
 *
 * <p>Closing it while something is running HIDES it; the import carries on and the uploader is notified.
 * Closing it once everything has finished forgets the jobs, because there is nothing left to come back to.
 */
export function ImportProgressTray() {
  const progress = useImportProgress();
  const [minimised, setMinimised] = useState(false);

  if (progress === null || !progress.visible || progress.tracked.length === 0) {
    return null;
  }

  const running = progress.tracked.filter((t) => !isImportJobFinished(t.job));
  const isRunning = running.length > 0;

  const title = isRunning
    ? running.length === 1
      ? 'Import in progress'
      : `${String(running.length)} imports in progress`
    : 'Imports completed';

  const note = isRunning
    ? "Running in the background. You can switch tabs or log out; the admin will be notified when it's done."
    : 'All records were imported. The admin has been notified.';

  return (
    <section className="ms-tray" aria-label="Import progress" aria-live="polite">
      <header className="ms-tray__head">
        {isRunning ? (
          <span className="ms-tray__spinner" aria-hidden="true" />
        ) : (
          <i className="pi pi-check-circle ms-tray__done-icon" aria-hidden="true" />
        )}
        <span className="ms-tray__title">{title}</span>
        <button
          type="button"
          className="ms-tray__action"
          aria-label={minimised ? 'Expand' : 'Minimise'}
          aria-expanded={!minimised}
          onClick={() => {
            setMinimised((was) => !was);
          }}
        >
          <i className={`pi ${minimised ? 'pi-chevron-up' : 'pi-chevron-down'}`} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="ms-tray__action"
          title={
            isRunning
              ? 'Close — the import continues in the background'
              : 'Close'
          }
          aria-label="Close"
          onClick={() => {
            /*
              Hide while something runs, forget once nothing does. Clearing a running job would leave the
              import going with no way back to it until the notification arrives.
            */
            if (isRunning) {
              progress.hide();
            } else {
              progress.clearFinished();
              progress.hide();
            }
          }}
        >
          <i className="pi pi-times" aria-hidden="true" />
        </button>
      </header>

      {minimised ? null : (
        <div className="ms-tray__body">
          <p className="ms-tray__note">{note}</p>
          {progress.tracked.map((entry) => (
            <TrayRow key={entry.job.jobId} entry={entry} />
          ))}
        </div>
      )}
    </section>
  );
}

function TrayRow({ entry }: { entry: TrackedImport }) {
  const { job } = entry;
  const [downloading, setDownloading] = useState(false);
  /* A download that failed is said here rather than thrown away: the tray is the only thing on screen. */
  const [failed, setFailed] = useState(false);
  const done = isImportJobFinished(job);
  const percent = job.read === 0 ? 0 : Math.round((job.processed / job.read) * 100);
  const batches = batchesOf(job);
  /*
    The batch being worked on, not the one last finished: a file 200 rows into 2,715 is in batch 2 of 11.
    Capped at the total so the last tick cannot read "batch 12 of 11".
  */
  const currentBatch = done
    ? batches
    : Math.min(batches, Math.floor(job.processed / Math.max(1, job.batchSize)) + 1);

  return (
    <div className="ms-tray__job">
      <div className="ms-tray__job-top">
        <span className="ms-tray__job-name">
          {job.master} · .{entry.format}
        </span>
        <span className={`ms-tray__pct${done ? ' ms-tray__pct--done' : ''}`}>{percent}%</span>
      </div>
      <span className="ms-tray__bar">
        <span
          className={`ms-tray__fill${done ? ' ms-tray__fill--done' : ''}`}
          style={{ width: `${String(percent)}%` }}
        />
      </span>
      <span className="ms-tray__meta">
        {done
          ? /*
               Both halves of the outcome: 1,990 of 2,000 says what landed AND what did not, where the
               imported count alone reads as the whole file on a run that rejected rows.
            */
            `${count(job.imported)}/${count(job.read)} records · ${String(batches)} ${batches === 1 ? 'batch' : 'batches'}`
          : `Batch ${String(currentBatch)} of ${String(batches)} · ${count(job.processed)} / ${count(job.read)}`}
      </span>
      {/*
        Said only once there is something to say, and only when the file has finished.

        The line IS the download where the master can produce one: the rejected rows are a file to open
        beside the original, and sending somebody to another screen to fetch it is a step with no purpose.
      */}
      {done && job.rejected > 0 ? (
        entry.downloadErrors === undefined ? (
          <span className="ms-tray__meta ms-tray__meta--warn">
            {count(job.rejected)} rejected — open the master to download the failed records
          </span>
        ) : (
          <button
            type="button"
            className="ms-tray__failed"
            disabled={downloading}
            onClick={() => {
              setDownloading(true);
              void entry
                .downloadErrors?.(job.jobId)
                .catch(() => {
                  setFailed(true);
                })
                .finally(() => {
                  setDownloading(false);
                });
            }}
          >
            <i
              className={`pi ${downloading ? 'pi-spin pi-spinner' : 'pi-download'}`}
              aria-hidden="true"
            />
            {count(job.rejected)} rejected — download the failed records
          </button>
        )
      ) : null}
      {failed ? (
        <span className="ms-tray__meta ms-tray__meta--warn">
          The failed records could not be downloaded. Open the master and try again.
        </span>
      ) : null}
    </div>
  );
}

/** How many batches this file takes, at the size the server told us it is using. */
function batchesOf(job: MasterImportJob): number {
  const size = Math.max(1, job.batchSize);
  return Math.max(1, Math.ceil(job.read / size));
}

/** Grouped: 2,715 rather than 2715, which is unreadable at five figures. */
function count(value: number): string {
  return value.toLocaleString();
}
